
import { supabase } from '@/integrations/supabase/client';
import { Company } from '@/lib/types';

export interface CompanyWebSearchResult {
  summary: string;
  overview?: string;
  history?: string;
  reputation?: string;
  timestamp: string;
  sources: Array<{
    url: string;
    title: string;
    indexRange?: string;
  }>;
  contactDetails?: {
    address?: string;
    phone?: string;
    email?: string;
    website?: string;
  };
  socialMedia?: {
    linkedin?: string;
    facebook?: string;
    twitter?: string;
    instagram?: string;
  };
  locations?: string[];
  industryInsights?: string[];
  marketPosition?: string;
  competitors?: string[];
  foundedYear?: number;
  companySize?: string;
  keyPeople?: string[];
  products?: string[];
  services?: string[];
  recentNews?: string[];
  linkedIn?: {
    companyPageUrl?: string;
    companyDescription?: string;
    phone?: string;
    email?: string;
    socialMedia?: any;
    employees?: any[];
  };
  places?: {
    rating?: number;
    reviewsCount?: number;
    address?: string;
    phone?: string;
    placeId?: string;
    mapsUrl?: string;
    reviews?: any[];
  };
  rawText?: string;
  [key: string]: any;
}

export type WebSearchStatus = 'pending' | 'processing' | 'completed' | 'failed';

export const enrichCompanyWithWebSearch = async (company: Company, forceRefresh: boolean = false): Promise<CompanyWebSearchResult | null> => {
  try {
    console.log(`Requesting web search enrichment for company: ${company.id}`);
    
    // First, check if we already have web search data and whether refresh is forced
    const existingData = await getCompanyWebSearchData(company.id);
    if (existingData && !forceRefresh) {
      console.log('Company already has web search data, updating status to completed');
      
      // Update status to completed if it's not already
      const { error: updateError } = await supabase
        .from('companies')
        .update({
          web_search_status: 'completed',
          updated_at: new Date().toISOString()
        })
        .eq('id', company.id);
        
      if (updateError) {
        console.error('Error updating company status:', updateError);
      }
      
      return existingData;
    }
    
    // If we're here, either there's no existing data or we're forcing a refresh
    // Update the company status to pending and set queue time
    const { error: updateError } = await supabase
      .from('companies')
      .update({
        web_search_status: 'pending',
        web_search_queue_time: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', company.id);

    if (updateError) {
      console.error('Error updating company status:', updateError);
      throw updateError;
    }
    
    // Invoke the enrich-company function with retry logic
    console.log('Invoking enrich-company function with company data:', {
      id: company.id,
      name: company.name,
      website: company.website,
      industry: company.industry
    });
    
    let retries = 3;
    let lastError: any = null;
    let data: any = null;

    while (retries > 0) {
      try {
        const result = await supabase.functions.invoke('enrich-company', {
          body: { 
            companyId: company.id,
            companyData: {
              name: company.name,
              website: company.website,
              industry: company.industry
            },
            forceRefresh: true
          }
        });
        
        if (!result.error) {
          data = result.data;
          break;
        }
        
        lastError = result.error;
        retries--;
        
        if (retries > 0) {
          console.log(`Retrying enrichment (${retries} attempts left)...`);
          await new Promise(r => setTimeout(r, 2000));
        }
      } catch (e) {
        lastError = e;
        retries--;
        if (retries > 0) {
          await new Promise(r => setTimeout(r, 2000));
        }
      }
    }

    if (lastError) {
      console.error('Error enriching company with web search after retries:', lastError);
      
      // Update status to failed if there's an error
      await supabase
        .from('companies')
        .update({
          web_search_status: 'failed',
          updated_at: new Date().toISOString()
        })
        .eq('id', company.id);
        
      throw lastError;
    }

    console.log('Web search enrichment triggered for company');
    return data?.data || null;
  } catch (error) {
    console.error('Error in enrichCompanyWithWebSearch:', error);
    return null;
  }
};

export const getCompanyWebSearchData = async (companyId: string): Promise<CompanyWebSearchResult | null> => {
  try {
    const { data, error } = await supabase
      .from('companies')
      .select('web_search_data, web_search_updated_at, web_search_status')
      .eq('id', companyId)
      .single();

    if (error) {
      console.error('Error fetching company web search data:', error);
      return null;
    }

    // If data exists but web_search_data is null, return null
    if (!data || !data.web_search_data) {
      return null;
    }

    // If data exists but status is not completed, update status to completed
    if (data.web_search_data && data.web_search_status !== 'completed') {
      await supabase
        .from('companies')
        .update({
          web_search_status: 'completed',
          updated_at: new Date().toISOString()
        })
        .eq('id', companyId);
    }

    const result = data.web_search_data as CompanyWebSearchResult & { _companyId?: string };
    result._companyId = companyId;
    return result;
  } catch (error) {
    console.error('Error in getCompanyWebSearchData:', error);
    return null;
  }
};

export const getCompanyWebSearchStatus = async (companyId: string): Promise<{
  status: WebSearchStatus;
  updatedAt: Date | null;
  queuedAt: Date | null;
} | null> => {
  try {
    const { data, error } = await supabase
      .from('companies')
      .select('web_search_status, web_search_updated_at, web_search_queue_time, web_search_data')
      .eq('id', companyId)
      .single();

    if (error || !data) {
      return null;
    }

    // If we have web_search_data but status isn't completed, consider it completed
    const effectiveStatus = (data.web_search_data && data.web_search_status !== 'completed') 
      ? 'completed' 
      : (data.web_search_status as WebSearchStatus || 'pending');

    return {
      status: effectiveStatus,
      updatedAt: data.web_search_updated_at ? new Date(data.web_search_updated_at) : null,
      queuedAt: data.web_search_queue_time ? new Date(data.web_search_queue_time) : null
    };
  } catch (error) {
    console.error('Error in getCompanyWebSearchStatus:', error);
    return null;
  }
};

export const generateEmailTemplate = async (companyId: string, leadId?: string): Promise<string | null> => {
  try {
    // Get company web search data
    const companyData = await getCompanyWebSearchData(companyId);
    if (!companyData) {
      return null;
    }
    
    // Get additional lead data if provided
    let leadData = null;
    if (leadId) {
      const { data, error } = await supabase
        .from('leads')
        .select('*')
        .eq('id', leadId)
        .single();
        
      if (!error && data) {
        leadData = data;
      }
    }
    
    // Call edge function to generate the email template
    const { data, error } = await supabase.functions.invoke('generate-template', {
      body: { 
        type: 'email',
        companyData,
        leadData
      }
    });

    if (error) {
      console.error('Error generating email template:', error);
      return null;
    }

    return data.template;
  } catch (error) {
    console.error('Error in generateEmailTemplate:', error);
    return null;
  }
};

export const generateCallScript = async (companyId: string, leadId?: string): Promise<string | null> => {
  try {
    // Get company web search data
    const companyData = await getCompanyWebSearchData(companyId);
    if (!companyData) {
      return null;
    }
    
    // Get additional lead data if provided
    let leadData = null;
    if (leadId) {
      const { data, error } = await supabase
        .from('leads')
        .select('*')
        .eq('id', leadId)
        .single();
        
      if (!error && data) {
        leadData = data;
      }
    }
    
    // Call edge function to generate the call script
    const { data, error } = await supabase.functions.invoke('generate-template', {
      body: { 
        type: 'call',
        companyData,
        leadData
      }
    });

    if (error) {
      console.error('Error generating call script:', error);
      return null;
    }

    return data.template;
  } catch (error) {
    console.error('Error in generateCallScript:', error);
    return null;
  }
};
